import type { Ref } from 'vue';
import type { AnnotationId } from '../../BaseAnnotation';
import type { Handler } from '../../provides';
import type EditAnnotationLayer from '../../layers/EditAnnotationLayer';
import type { EditAnnotationTypes } from '../../layers/EditAnnotationLayer';
import type RectangleLayer from '../../layers/AnnotationLayers/RectangleLayer';
import type PolygonLayer from '../../layers/AnnotationLayers/PolygonLayer';
import type LineLayer from '../../layers/AnnotationLayers/LineLayer';
import type { LayerManagerAlignedView } from './useLayerManagerAlignedView';
import routeMulticamEditToCamera from './useMulticamEditRouting';
import type CameraStore from '../../CameraStore';
import { pointInPolygon } from '../../utils';
import pickPolygon from './polygonSelection';

export default function useAnnotationClickHandling(options: {
  camera: string;
  handler: Handler;
  selectedCamera: Ref<string>;
  selectedTrackIdRef: Ref<AnnotationId | null>;
  selectedKeyRef: Ref<string>;
  frameNumberRef: Ref<number>;
  flickNumberRef: Ref<number>;
  editingModeRef: Ref<false | EditAnnotationTypes>;
  cameraStore: CameraStore;
  alignedView: Pick<
    LayerManagerAlignedView,
    'alignedDisplayInverse' | 'mapNativePoint' | 'mapEditGeoJSONToNative'
  >;
  editAnnotationLayer: EditAnnotationLayer;
  boxEditLayer?: EditAnnotationLayer;
  rectAnnotationLayer: RectangleLayer;
  polyAnnotationLayer: PolygonLayer;
  lineLayer: LineLayer;
  refreshLayers: () => void;
}) {
  const {
    camera,
    handler,
    selectedCamera,
    selectedTrackIdRef,
    selectedKeyRef,
    frameNumberRef,
    flickNumberRef,
    editingModeRef,
    cameraStore,
    alignedView,
    editAnnotationLayer,
    boxEditLayer,
    rectAnnotationLayer,
    polyAnnotationLayer,
    lineLayer,
    refreshLayers,
  } = options;

  // Set briefly when a draw finalizes so the same click doesn't also select
  // an overlapping existing detection. Cleared on the next macrotask.
  let justFinalizedCreation = false;

  // Guards against a single physical click being handled more than once when
  // it lands on overlapping features on different layers.
  let clickHandledThisTick = false;

  const clicked = (trackId: number, editing: boolean, modifiers?: { ctrl: boolean }, geo?: { x: number; y: number }) => {
    if (justFinalizedCreation) {
      return;
    }
    if (clickHandledThisTick) {
      return;
    }
    clickHandledThisTick = true;
    window.setTimeout(() => { clickHandledThisTick = false; }, 0);

    if (editing && trackId !== null && geo && editAnnotationLayer.type === 'Polygon') {
      if (selectedCamera.value !== camera) handler.selectCamera(camera, false);
      if (selectedCamera.value !== camera) return;
      const point = alignedView.mapNativePoint(geo.x, geo.y);
      const polygon = pickPolygon(polyAnnotationLayer.formattedData, trackId, point, true);
      if (polygon) {
        editAnnotationLayer.disable();
        handler.trackSelect(trackId, true);
        finishPolygonClick(trackId, polygon.polygonKey, true);
        return;
      }
    }

    if (selectedCamera.value !== camera) {
      if (editAnnotationLayer.getMode() === 'creation' && !editing) {
        return;
      }
      if (trackId !== null) {
        handler.selectCamera(camera, false);
        if (selectedCamera.value === camera) {
          handler.trackSelect(trackId, false, modifiers);
          if (editing) {
            handler.trackEdit(trackId);
          }
        }
      }
      return;
    }
    if (editAnnotationLayer.getMode() !== 'creation') {
      editAnnotationLayer.disable();
      if (editing && trackId !== null) {
        handler.trackEdit(trackId);
      } else {
        handler.trackSelect(trackId, editing, modifiers);
      }
    } else if (editing && trackId !== null) {
      editAnnotationLayer.disable();
      handler.trackEdit(trackId);
    }
  };

  // A right-click on the detection being edited, from a camera that is not
  // selected, moves the edit to that camera instead of finishing it. The
  // editor there leaves edit mode on that same click (before or after this
  // runs), so the edit is re-entered once every layer has handled it.
  let editMovingHere = false;
  const editedRightClicked = (trackId: number) => {
    if (editMovingHere || selectedCamera.value === camera || trackId !== selectedTrackIdRef.value
        || editAnnotationLayer.type === 'Polygon') {
      return;
    }
    handler.selectCamera(camera, false);
    if (selectedCamera.value !== camera) return;
    editMovingHere = true;
    window.setTimeout(() => {
      editMovingHere = false;
      if (selectedCamera.value !== camera || selectedTrackIdRef.value !== trackId) return;
      if (!editingModeRef.value) handler.trackSelect(trackId, true);
      refreshLayers();
    }, 0);
  };

  // GeoJS may finish the current edit later in the same mouse event. Apply
  // polygon navigation after all layers have processed that click.
  let polygonNavigationPending = false;
  function finishPolygonClick(trackId: AnnotationId, polygonKey?: string, enterEditing = false) {
    if (polygonNavigationPending) return;
    polygonNavigationPending = true;
    const frame = frameNumberRef.value;
    const switchPolygon = enterEditing || (polygonKey !== undefined && polygonKey !== selectedKeyRef.value);
    window.setTimeout(() => {
      polygonNavigationPending = false;
      if (selectedCamera.value !== camera || frameNumberRef.value !== frame
          || selectedTrackIdRef.value !== trackId) return;
      editAnnotationLayer.disable();
      if (polygonKey !== undefined) handler.selectFeatureHandle(-1, polygonKey);
      // Select explicitly: trackEdit toggles the whole detection off when it
      // was already being edited, requiring an unwanted second right-click.
      handler.trackSelect(trackId, switchPolygon);
      refreshLayers();
    }, 0);
  }

  function wireHandlers() {
    editAnnotationLayer.bus.$on('editing-annotation-sync', (editing: boolean, deselect?: boolean) => {
      if (deselect) {
        handler.trackSelect(null, false);
      } else if (!(editMovingHere && !editing)) {
        handler.trackSelect(selectedTrackIdRef.value, editing);
      }
    });
    editAnnotationLayer.bus.$on('polygon-edit-right-click', (geo: { x: number; y: number }) => {
      const trackId = selectedTrackIdRef.value;
      if (trackId === null || editingModeRef.value !== 'Polygon') return;
      // The editor that took the click is live on every camera holding the
      // detection, so navigate its polygons here after selecting this camera.
      // Moving to this camera keeps the edit going even on the same polygon;
      // a gap or hole still finishes it.
      const switching = selectedCamera.value !== camera;
      if (switching) handler.selectCamera(camera, false);
      if (selectedCamera.value !== camera) return;
      const point = alignedView.mapNativePoint(geo.x, geo.y);
      const hit = pickPolygon(polyAnnotationLayer.formattedData, trackId as number, point);
      finishPolygonClick(trackId, hit?.polygonKey, switching && hit != null);
    });

    // On Linux the contextmenu arrives before the button is released, and
    // GeoJS reports the click on release: confirming (leaving edit mode)
    // before then makes that click read as a right-click on an unedited
    // detection, which re-enters editing. So confirm once the button is up.
    const afterRelease = (buttonHeld: boolean, action: () => void) => {
      const run = () => window.setTimeout(action, 0);
      if (buttonHeld) document.addEventListener('mouseup', run, { once: true });
      else run();
    };

    // Only the selected camera's editor may confirm: the other cameras keep a
    // live creation editor too, and a right-click on one of them is a move of
    // the edit, not a confirmation.
    editAnnotationLayer.bus.$on('confirm-annotation', (buttonHeld: boolean) => {
      if (selectedCamera.value !== camera) return;
      afterRelease(buttonHeld, () => handler.confirmRecipe());
    });

    // Lock this camera's mask without deselecting, then finish the edit as a
    // plain right-click would unless the other camera took it over.
    editAnnotationLayer.bus.$on('confirm-annotation-elsewhere', (buttonHeld: boolean) => {
      if (selectedCamera.value !== camera) return;
      handler.segmentationFinalizePending();
      afterRelease(buttonHeld, () => {
        if (selectedCamera.value === camera) handler.confirmRecipe();
      });
    });
    handler.registerFinalizeCreation(() => {
      editAnnotationLayer.finalizeInProgress();
    });

    rectAnnotationLayer.bus.$on('annotation-clicked', clicked);
    rectAnnotationLayer.bus.$on('annotation-right-clicked', clicked);
    rectAnnotationLayer.bus.$on('annotation-ctrl-clicked', clicked);
    rectAnnotationLayer.bus.$on('edited-annotation-right-clicked', editedRightClicked);
    polyAnnotationLayer.bus.$on('annotation-clicked', clicked);
    polyAnnotationLayer.bus.$on('annotation-right-clicked', clicked);
    polyAnnotationLayer.bus.$on('annotation-ctrl-clicked', clicked);
    polyAnnotationLayer.bus.$on('edited-annotation-right-clicked', editedRightClicked);
    lineLayer.bus.$on('annotation-clicked', clicked);
    lineLayer.bus.$on('annotation-right-clicked', clicked);
    lineLayer.bus.$on('edited-annotation-right-clicked', editedRightClicked);

    polyAnnotationLayer.bus.$on('polygon-right-clicked', (trackId: number, polygonKey: string) => {
      // Visible masks must not replace the centerline's key when editing a line.
      if (editingModeRef.value === 'LineString'
          || (editAnnotationLayer.type === 'LineString' && editAnnotationLayer.getMode() !== 'disabled')) return;
      if (polygonNavigationPending) return;
      if (trackId === selectedTrackIdRef.value
          && editingModeRef.value === 'Polygon' && editAnnotationLayer.getMode() !== 'creation') {
        // The edit-layer click resolves the actual polygon hit (including
        // holes) and applies the switch after GeoJS finishes this mouse event,
        // on whichever camera's editor holds the detection.
        return;
      }
      if (editAnnotationLayer.getMode() === 'creation') {
        handler.cancelCreation();
      }
      handler.selectFeatureHandle(-1, polygonKey);
      window.setTimeout(() => refreshLayers(), 0);
    });

    polyAnnotationLayer.bus.$on('polygon-clicked', (_trackId: number, polygonKey: string) => {
      if (editingModeRef.value === 'LineString'
          || (editAnnotationLayer.type === 'LineString' && editAnnotationLayer.getMode() !== 'disabled')) return;
      if (editAnnotationLayer.getMode() === 'creation') {
        return;
      }
      handler.selectFeatureHandle(-1, polygonKey);
      window.setTimeout(() => refreshLayers(), 0);
    });

    polyAnnotationLayer.bus.$on('polygon-right-clicked-outside', () => {
      if (editingModeRef.value === 'LineString'
          || (editAnnotationLayer.type === 'LineString' && editAnnotationLayer.getMode() !== 'disabled')) return;
      if (selectedTrackIdRef.value !== null
          && editingModeRef.value === 'Polygon' && editAnnotationLayer.getMode() !== 'creation') {
        // The edit layer also receives clicks in gaps between polygons.
        return;
      }
      if (editAnnotationLayer.getMode() === 'creation') {
        handler.cancelCreation();
        handler.selectFeatureHandle(-1, '');
        window.setTimeout(() => refreshLayers(), 0);
      }
    });

    const updateGeoJSON = (
      mode: 'in-progress' | 'editing',
      geometryCompleteEvent: boolean,
      data: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.LineString | GeoJSON.Point>,
      type: string,
      key = '',
      cb: () => void = () => (undefined),
    ) => {
      if (routeMulticamEditToCamera({
        camera,
        selectedCamera,
        frameNumberRef,
        selectedTrackIdRef,
        editingModeRef,
        selectedKeyRef,
        cameraStore,
        handler,
      })) {
        return;
      }

      const inverse = alignedView.alignedDisplayInverse.value;
      const mapped = alignedView.mapEditGeoJSONToNative(inverse, data, type);
      if (type === 'rectangle' && mapped.bounds) {
        cb();
        handler.updateRectBounds(
          frameNumberRef.value,
          flickNumberRef.value,
          mapped.bounds,
          mapped.rotation,
        );
      } else if (mapped.geoJSON) {
        handler.updateGeoJSON(
          mode,
          frameNumberRef.value,
          flickNumberRef.value,
          mapped.geoJSON,
          key,
          cb,
        );
      }
      if (geometryCompleteEvent) {
        justFinalizedCreation = true;
        window.setTimeout(() => { justFinalizedCreation = false; }, 0);
        refreshLayers();
      }
    };
    editAnnotationLayer.bus.$on('update:geojson', updateGeoJSON);
    // A box edit leaves the line untouched, so the line layer keeps its
    // annotation (and any selected vertex) rather than rebuilding.
    boxEditLayer?.bus.$on('update:geojson', (
      mode: 'in-progress' | 'editing',
      geometryCompleteEvent: boolean,
      data: GeoJSON.Feature<GeoJSON.Polygon>,
      type: string,
      key = '',
      cb: () => void = () => (undefined),
    ) => updateGeoJSON(mode, geometryCompleteEvent, data, type, key, () => {
      cb();
      editAnnotationLayer.skipNextExternalUpdate = true;
    }));

    editAnnotationLayer.bus.$on(
      'update:selectedIndex',
      (index: number, _type: EditAnnotationTypes, key?: string) => {
        if (index >= 0 && key !== undefined) {
          handler.selectFeatureHandle(index, key);
        } else {
          handler.selectFeatureHandle(index, selectedKeyRef.value);
        }
      },
    );

    editAnnotationLayer.bus.$on('click-outside-edit', (geo: { x: number; y: number }) => {
      const point = alignedView.mapNativePoint(geo.x, geo.y);
      const polygonData = polyAnnotationLayer.formattedData;
      const clickedPolygon = polygonData.find((item) => {
        const rings = item.polygon.coordinates;
        const outer = rings[0].map(([x, y]) => ({ x, y }));
        const holes = rings.slice(1).map((ring) => ring.map(([x, y]) => ({ x, y })));
        return pointInPolygon({ x: point[0], y: point[1] }, outer, holes);
      });
      if (clickedPolygon) {
        handler.selectFeatureHandle(-1, clickedPolygon.polygonKey || '');
        window.setTimeout(() => refreshLayers(), 0);
      }
    });
  }

  return { wireHandlers };
}
